import React, { FC, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Button from '../../../Button/Button';
import Chip from '../../../Chip/Chip';
import { cn } from '../../../lib/utils';
import Typography from '../../../Typography/Typography';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui';
import { MEDIA_TYPE_OPTIONS } from '../constants';

import { MediaContentItemProps } from './types';

const MediaContentItem: FC<MediaContentItemProps> = ({
  handleImageDelete,
  name,
  url,
  type,
  mediaId,
  mimeType,
  isSelected,
  onSelect,
  onDoubleClick,
  setIsCopying
}) => {
  const [open, setOpen] = useState(false);
  const extension = mimeType?.trim().replace(/^image\//, '');

  const MEDIA_ITEM_OPTIONS = [
    {
      name: 'Copy URL',
      dataTestid: 'MediaContentItem-CopyURL',
      action: () => {
        navigator.clipboard.writeText(url);
        setIsCopying(true);
        setOpen(false);
      }
    },
    {
      name: 'Delete',
      dataTestid: 'MediaContentItem-Delete',
      action: () => {
        handleImageDelete(mediaId);
        setOpen(false);
      }
    }
  ];

  return (
    <div
      data-testid={`MediaContentItem-${mediaId}`}
      className={cn(
        'group border border-transparent hover:cursor-pointer hover:shadow-sm hover:border hover:border-base-border rounded-xl py-3 px-2.5  w-full text-center',
        isSelected && 'border-indigo-700 hover:border-indigo-700'
      )}
      onMouseLeave={() => {
        setOpen(false);
      }}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
    >
      <div
        data-testid={`MediaContentItem-Image-${mediaId}`}
        className="w-full h-[120px] border border-base-border rounded-xl bg-base-accent mb-2 bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{ backgroundImage: `url(${url})` }}
      >
        <div className="h-full justify-between pt-1.5 px-2 hidden group-hover:flex group-hover:bg-image-upload-hover">
          <Chip className="w-[70px] inline-block truncate justify-start py-1 capitalize">
            {MEDIA_TYPE_OPTIONS.find(({ value }) => value === type)!.label}
          </Chip>
          <Popover onOpenChange={setOpen} open={open}>
            <PopoverTrigger asChild>
              <Button
                data-testid="MediaContentItem-EllipsisButton"
                onClick={() => {
                  setOpen(true);
                }}
                className="rounded-lg bg-transparent hover:bg-[rgba(255,255,255,0.30)] p-1 h-6"
                rightIcon={
                  <FontAwesomeIcon
                    icon="fa-regular fa-ellipsis-vertical"
                    className="text-base-sidebarBorder text-p-xs"
                  />
                }
              />
            </PopoverTrigger>
            <PopoverContent
              data-testid={`MediaContentItem-Popover-${mediaId}`}
              align="end"
              className="w-[100px] bg-base-popover rounded-md shadow-md border border-base-border p-1"
            >
              {MEDIA_ITEM_OPTIONS.map(({ name, action, dataTestid }) => (
                <Typography
                  key={name}
                  className="text-p-s text-base-popoverForeground text-left px-2 py-1.5 hover:bg-base-accent rounded-sm"
                  onClick={action}
                  data-testid={dataTestid}
                >
                  {name}
                </Typography>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <Typography
        className="text-base-mutedForeground text-p-s truncate mx-0.5"
        data-testid={`MediaContentItem-Name-${mediaId}`}
      >
        {name}
      </Typography>
      <Typography
        className="truncate text-p-s text-base-foreground mx-0.5 uppercase"
        data-testid={`MediaContentItem-Extension-${mediaId}`}
      >
        {extension}
      </Typography>
    </div>
  );
};

export default MediaContentItem;
